import { ORDER_DAY_CALL_AHEADS, orderDayNeed, type OrderDay, type OrderDayRow } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ORDER_DAY_STATE } from './InvoiceNewPage';
import { api } from '../api';
import { VendorShorts } from '../components/VendorShorts';
import { useAuth } from '../auth';
import { Sheet, type SheetColumn } from '../components/Sheet';
import { Empty, ErrorBox, QueryView } from '../components/ui';
import { addDays, formatDate, formatMoney, formatQty, todayIso } from '../format';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';
import { parseNumber } from './StockPage';

/** A row of the sheet: the server's row plus what was typed and not saved yet. */
type Line = OrderDayRow & { typed: Record<string, number | null>; zeroed: Record<string, true>; bad?: Record<string, string> };
type Account = OrderDay['accounts'][number];
const LOCKED = new Set(['picked', 'out_for_delivery', 'delivered', 'invoiced', 'cancelled']);
const isLocked = (a: Account) => !!a.orderStatus && LOCKED.has(a.orderStatus);
const round1 = (n: number) => Math.round(n * 10) / 10;

/** What a store cell holds now: what was typed, a guess the office set to none, else the order (or the standing order or the pattern's guess). */
export function cellQty(l: Line, ai: number, a: Account): number {
  const typed = l.typed[a.customerId];
  if (typed !== undefined) return typed ?? 0;
  if (l.zeroed[a.customerId]) return 0;
  return l.accounts[ai]!.qty;
}
/** The stores' total and the ORDER, recomputed as the office types, with the server's own rounding. */
export function liveTotals(l: Line, accounts: Account[]): { stores: number; order: number } {
  // keyMore: a key store's second order in the window, or the standing quantity of its days without an order
  const stores = round1(accounts.reduce((s, a, i) => s + cellQty(l, i, a), 0) + (l.keyMore ?? 0));
  // the same formula as the server (decision 82): the key accounts add up; the Other columns and More others are
  // booked demand of the customers the base already forecasts, so they count only beyond it
  const keyStores = accounts.reduce((s, a, i) => s + (a.extra ? 0 : cellQty(l, i, a)), 0) + (l.keyMore ?? 0);
  const booked = accounts.reduce((s, a, i) => s + (a.extra ? cellQty(l, i, a) : 0), 0) + l.others;
  return { stores, order: orderDayNeed({ base: l.base, safety: l.safety, keep: l.keep, keyStores, booked, onHand: l.onHand + (l.invoicedBack ?? 0) }) };
}

/**
 * The PLAN tab of a load (decision 76): an item per row, a column per key account (the Mezquite stores) typed like
 * cells, and the ORDER the model wants. The ORDER is bought on the vendor tabs beside this one.
 */
export function OrderDayPage({ load, date, extra = null, onExtra }: { load: number; date: string; extra?: string | null; onExtra?: (ids: string[]) => Promise<void> }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const { me } = useAuth();
  const books = me?.role === 'owner' || me?.role === 'office';
  /** `${itemId}:${customerId}` -> typed into a store column, not saved yet */
  const [cells, setCells] = useState<Record<string, number | null>>({});
  /** `${itemId}:${customerId}` -> a guess (pattern or standing order) the office set to none; there is no order to save it to */
  const [zeroed, setZeroed] = useState<Record<string, true>>({});
  /** `${itemId}:${customerId}` -> text typed that is not a number ("5o", "-2"): kept on screen in red, Save waits */
  const [badCells, setBadCells] = useState<Record<string, string>>({});
  const [q, setQ] = useState('');
  const [onlyNumbers, setOnlyNumbers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [note, setNote] = useState<string | null>(null);
  /** the item whose vendor the office is choosing for this load (decision 154) */
  const [moving, setMoving] = useState<Line | null>(null);
  /** a call-ahead column being added, kept or taken off (decision 154) */
  const [columnBusy, setColumnBusy] = useState(false);
  const [columnError, setColumnError] = useState<unknown>(null);
  const key = `${date}:${load}:${extra ?? 'auto'}`;
  const day = useQuery({ queryKey: qk.orderDay(key), queryFn: ({ signal }) => api.orderDay({ date, load, extra }, signal) });
  // the customers a call-ahead column can be set to: anyone who is not a key account
  const people = useQuery({ queryKey: qk.customers('', ''), queryFn: ({ signal }) => api.customers('', signal), enabled: books && !!onExtra, staleTime: 5 * 60_000 });
  const d = day.data;
  const accounts = useMemo(() => d?.accounts ?? [], [d]);
  const lockedIds = new Set(accounts.filter(isLocked).map((a) => a.customerId));
  const cellKeys = Object.keys(cells);
  const unsavedKeys = cellKeys.filter((k) => !lockedIds.has(k.split(':')[1]!));
  const lockedTyped = accounts.filter((a) => isLocked(a) && cellKeys.some((k) => k.endsWith(`:${a.customerId}`)));
  const unsaved = unsavedKeys.length;
  // a typo on a store that got locked meanwhile (picked) cannot be fixed there and does not hold the others back
  const badCount = Object.keys(badCells).filter((k) => !lockedIds.has(k.split(':')[1]!)).length;
  const dirty = cellKeys.length > 0 || badCount > 0;

  const lines: Line[] = useMemo(
    () =>
      (d?.rows ?? []).map((r) => ({
        ...r,
        typed: Object.fromEntries(accounts.filter((a) => `${r.itemId}:${a.customerId}` in cells).map((a) => [a.customerId, cells[`${r.itemId}:${a.customerId}`]!])),
        zeroed: Object.fromEntries(accounts.filter((a) => zeroed[`${r.itemId}:${a.customerId}`]).map((a) => [a.customerId, true as const])),
        bad: Object.fromEntries(accounts.filter((a) => `${r.itemId}:${a.customerId}` in badCells).map((a) => [a.customerId, badCells[`${r.itemId}:${a.customerId}`]!])),
      })),
    [d, accounts, cells, zeroed, badCells],
  );
  const words = q.trim().toLowerCase();
  const shown = lines.filter((l) => {
    if (words && !`${l.itemName} ${l.vendorName ?? ''}`.toLowerCase().includes(words)) return false;
    if (!onlyNumbers) return true;
    if (Object.keys(l.bad ?? {}).length > 0) return true; // a red cell stays in sight until it is fixed
    const live = liveTotals(l, accounts);
    return live.stores > 0 || live.order > 0;
  });

  /**
   * Save every store with typed numbers, one store at a time. Only the numbers typed are sent (merge): the rest of the
   * store's order stays as it is in the database, whoever changed it meanwhile. The grid is locked while saving, and only
   * the numbers that went out are cleared, so nothing typed is ever lost.
   */
  async function saveAll() {
    if (!d || unsaved === 0 || saving || badCount > 0) return;
    const sent = { ...cells };
    setSaving(true);
    setSaveError(null);
    try {
      for (const a of d.accounts) {
        if (isLocked(a)) continue;
        const mine = Object.entries(sent).filter(([k]) => k.endsWith(`:${a.customerId}`));
        if (mine.length === 0) continue;
        const next = await api.orderDayAccount(a.customerId, { date, load, merge: true, lines: mine.map(([k, v]) => ({ itemId: k.split(':')[0]!, qty: v ?? 0 })) });
        qc.setQueryData(qk.orderDay(key), next);
        const doneKeys = new Set(mine.map(([k]) => k));
        setCells((cur) => Object.fromEntries(Object.entries(cur).filter(([k, v]) => !(doneKeys.has(k) && v === sent[k]))));
      }
      setNote(t('allSaved'));
      void qc.invalidateQueries({ queryKey: ['orders'] });
      // the vendor tabs' model follows the stores' numbers
      void qc.invalidateQueries({ queryKey: ['forecast', 'vendor-sheets'] });
    } catch (e) {
      setSaveError(e);
      void qc.invalidateQueries({ queryKey: qk.orderDay(key) });
    } finally {
      setSaving(false);
    }
  }
  const saveRef = useRef(saveAll);
  saveRef.current = saveAll;

  // Ctrl/Cmd+S saves; leaving with numbers not saved (a link, a reload, closing the tab) asks first
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        const active = document.activeElement;
        if (active instanceof HTMLElement && active.closest('.sheet')) active.blur();
        setTimeout(() => void saveRef.current(), 0);
      }
    };
    const onUnload = (e: BeforeUnloadEvent) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    const onClick = (e: MouseEvent) => {
      // a link, or a button that changes the page (the load week) and would drop what was typed
      const a = e.target instanceof Element ? e.target.closest('a[href], [data-leaves]') : null;
      if (!dirty || !a) return;
      if (!window.confirm(t('orderDayUnsaved'))) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('beforeunload', onUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('beforeunload', onUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [dirty, t]);

  // JL: "when we press invoice the accounts, i want to be presented with a summary of all the invoices that will be
  // created ... editable" (decision 175): the invoices open side by side on the invoice screen, one per order, to check
  // and change (a store that called to add five corn) before one save makes them all
  const navigate = useNavigate();
  const location = useLocation();
  const invoiceAll = useMutation({
    mutationFn: async () => {
      // the standing orders the plan counted (bought for) are drafted first, as Standing orders → Create drafts does
      // (one order per standing order per day, safe to repeat), so they are invoiced with the rest (end-user test: a
      // standing order was bought for, never invoiced and never delivered)
      // never a day already gone: a load opened on an earlier date drafts nothing back in time
      const held = new Set<string>();
      if (d) {
        const today = todayIso();
        for (let day = d.sellFrom < today ? today : d.sellFrom; day <= d.sellTo; day = addDays(day, 1)) {
          for (const name of (await api.runStanding(day, true)).heldCustomers ?? []) held.add(name);
        }
      }
      return { orders: await api.orderDayInvoicePreview({ date, load, extra: accounts.filter((a) => a.extra).map((a) => a.customerId) }), held: [...held] };
    },
    onSuccess: ({ orders, held }) => {
      // a standing customer on hold or over the limit got no order: named, so the office takes it by hand if it should go
      const heldText = held.length ? ` ${t('standingHeld').replace('{names}', held.join(', '))}` : '';
      if (!orders.some((o) => !o.problem && o.lines.length > 0)) {
        setNote(`${t('nothingToInvoice')}${orders.length ? `: ${orders.map((o) => `${o.customerName}: ${o.problem ?? t('noLines')}`).join(' | ')}` : ''}${heldText}`);
        return;
      }
      navigate('/invoices/new', { state: { [ORDER_DAY_STATE]: { orders, held, back: `${location.pathname}${location.search}` } } });
    },
  });

  const num = (v: string): number | null | false => {
    if (v.trim() === '') return null;
    const n = parseNumber(v);
    return n === null || n < 0 ? false : n;
  };
  const extras = accounts.filter((a) => a.extra);
  const moreOthers = (d?.rows ?? []).some((r) => r.others > 0);
  const keyCount = accounts.length - extras.length;
  const typedIn = (customerId: string) => cellKeys.some((k) => k.endsWith(`:${customerId}`));
  /**
   * The call-ahead columns the office keeps on this load (decision 154): `slot` set to a customer ('' takes a kept column
   * off, KEEP keeps a column that shows only because of an order); a slot past the last adds a column. Only the kept
   * ones are saved: a customer with an order shows by itself.
   */
  const pick = async (slot: number, value: string) => {
    if (!onExtra || columnBusy) return;
    const cur = extras[slot];
    // the column leaves the screen (unless its order shows it anyway): its typed numbers go with it
    const goes = cur && value !== KEEP && !cur.byOrder;
    if (goes && typedIn(cur.customerId) && !window.confirm(t('orderDayUnsaved'))) return;
    const kept = extras.filter((a) => a.kept).map((a) => a.customerId);
    let ids: string[];
    if (value === KEEP) ids = cur ? [...kept, cur.customerId] : kept;
    else if (!cur) ids = [...kept, value];
    else ids = kept.flatMap((id) => (id === cur.customerId ? (value ? [value] : []) : [id]));
    setColumnBusy(true);
    setColumnError(null);
    try {
      await onExtra(ids.filter(Boolean));
      if (goes) setCells((c) => Object.fromEntries(Object.entries(c).filter(([k]) => !k.endsWith(`:${cur.customerId}`))));
    } catch (e) {
      setColumnError(e);
    } finally {
      setColumnBusy(false);
    }
  };
  // one empty column to add a call-ahead customer, up to eight (decision 154)
  const freeSlots = onExtra && extras.length < MAX_CALL_AHEADS ? 1 : 0;
  const picker = (slot: number) => {
    const cur = extras[slot];
    const taken = new Set(accounts.map((a) => a.customerId));
    const others = (people.data ?? []).filter((c) => c.active && !c.keyAccount && !taken.has(c.id));
    return (
      <>
        <span className="th-label">{cur ? `${t('callAheadCol')} ${slot + 1}` : `+ ${t('callAheadAdd')}`}</span>
        <select
          className="th-pick"
          aria-label={cur ? `${t('callAheadCol')} ${slot + 1}` : t('callAheadAdd')}
          value={cur?.customerId ?? ''}
          disabled={!onExtra || saving || columnBusy}
          onChange={(e) => void pick(slot, e.target.value)}
        >
          {cur ? null : <option value="">{t('callAheadPick')}</option>}
          {cur ? <option value={cur.customerId}>{cur.shortName ?? cur.customerName}</option> : null}
          {cur && !cur.kept ? <option value={KEEP}>{t('callAheadKeep')}</option> : null}
          {cur?.kept ? <option value="">{cur.byOrder ? t('callAheadUnkeep') : t('callAheadRemove')}</option> : null}
          {!cur || cur.kept
            ? others.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.shortName ?? c.name}
                </option>
              ))
            : null}
        </select>
      </>
    );
  };
  const accountHead = (a: Account) => (
    <span>
      {a.extra ? picker(extras.indexOf(a)) : null}
      {a.extra ? null : (
        <Link to={`/customers/${a.customerId}`} title={a.customerName}>
          {a.shortName ?? a.customerName}
        </Link>
      )}
      <span className="th-sub">
        {a.orderId ? (
          <Link to={`/orders/${a.orderId}`}>{isLocked(a) ? t(`status_${a.orderStatus}` as 'status_invoiced') : `${t('orderIn')}${a.orderNo ? ` #${a.orderNo}` : ''}`}</Link>
        ) : a.hasOrder ? (
          t('orderIn')
        ) : a.nextExpectedOn ? (
          `${t('expected')} ${formatDate(a.nextExpectedOn, intlLocale)}`
        ) : (
          t('noOrderYet')
        )}
      </span>
    </span>
  );
  const columns: SheetColumn<Line>[] = [
    {
      key: 'item',
      header: t('itemCol'),
      width: '150px',
      render: (l) => (
        <span title={[l.vendorName, ...(l.alsoAt ?? []).map((a) => `${a.vendorName} ${formatQty(a.qty)}`)].filter(Boolean).join(' · ') || undefined}>
          {l.itemName}
          {books ? (
            <>
              <span className="cell-vendor"> · </span>
              <button type="button" className="cell-vendor link-btn" title={t('buyFromHint')} onClick={() => setMoving(l)}>
                {l.vendorName ?? t('buyFromPick')}
              </button>
            </>
          ) : l.vendorName ? (
            <span className="cell-vendor"> · {l.vendorName}</span>
          ) : null}
          {/* part of ORDER is already on another vendor's order for this load */}
          {(l.alsoAt ?? []).length ? <span className="cell-vendor"> ({(l.alsoAt ?? []).map((a) => `${formatQty(a.qty)} ${a.vendorName}`).join(', ')})</span> : null}
        </span>
      ),
    },
    { key: 'base', header: t('base'), hint: t('baseHint'), width: '44px', align: 'right', render: (l) => (l.base ? formatQty(l.base) : '') },
    { key: 'safety', header: t('safety'), hint: t('safetyHint'), width: '52px', align: 'right', render: (l) => (l.safety ? formatQty(l.safety) : '') },
    { key: 'keep', header: t('keep'), width: '40px', align: 'right', render: (l) => (l.keep ? formatQty(l.keep) : '') },
    ...accounts.map(
      (a, ai): SheetColumn<Line> => ({
        key: `acct:${a.customerId}`,
        header: accountHead(a),
        label: a.shortName ?? a.customerName,
        // proportions: the sheet fills the screen, so a wide monitor makes every column wider (decision 164)
        width: a.extra ? '80px' : '68px',
        align: 'right',
        render: (l) => {
          if (l.bad?.[a.customerId] !== undefined) return l.bad[a.customerId];
          const v = cellQty(l, ai, a);
          return v ? formatQty(v) : '';
        },
        edit: books
          ? {
              kind: 'number',
              get: (l) => {
                if (l.bad?.[a.customerId] !== undefined) return l.bad[a.customerId]!;
                const v = cellQty(l, ai, a);
                return v ? String(v) : '';
              },
              set: (l, v) => {
                const n = num(v);
                // not a number: kept as typed, in red, instead of quietly going back to the old number
                if (n === false) return { ...l, bad: { ...l.bad, [a.customerId]: v } };
                const { [a.customerId]: _was, ...bad } = l.bad ?? {};
                return { ...l, typed: { ...l.typed, [a.customerId]: n }, bad };
              },
            }
          : undefined,
        canEdit: () => !isLocked(a) && !saving,
        cellClass: (l) => {
          if (l.bad?.[a.customerId] !== undefined) return 'unsaved bad';
          if (l.typed[a.customerId] !== undefined) return 'unsaved';
          if (l.zeroed[a.customerId]) return 'src-zeroed';
          // on the store's order with no price for it: cannot be invoiced (decision 162)
          if (l.accounts[ai]!.noPrice) return 'bad';
          const src = l.accounts[ai]!.source;
          return src === 'standing' || src === 'pattern' ? `src-${src}` : undefined;
        },
      }),
    ),
    // the call-ahead columns not set to anyone yet: pick the customer, then type
    ...(onExtra
      ? Array.from({ length: freeSlots }, (_, i): SheetColumn<Line> => ({
          key: `slot:${extras.length + i}`,
          header: <span>{picker(extras.length + i)}</span>,
          label: t('callAheadAdd'),
          width: '80px',
          align: 'right',
          render: () => '',
        }))
      : []),
    { key: 'stores', header: t('storesCol'), hint: t('accountsTotal'), width: '52px', align: 'right', render: (l) => { const s = liveTotals(l, accounts).stores; return s ? <strong title={l.keyMore ? t('keyMoreHint').replace('{n}', formatQty(l.keyMore)) : undefined}>{formatQty(s)}{l.keyMore ? '*' : ''}</strong> : ''; } },
    // a third call-ahead and beyond: shown only when there is one, so the two Other columns are the usual picture
    ...(moreOthers
      ? [{ key: 'others', header: t('moreOthersCol'), hint: t('moreOthersHint'), width: '48px', align: 'right' as const, render: (l: Line) => (l.others ? formatQty(l.others) : '') }]
      : []),
    // below zero: a receipt or a count is missing; ORDER counts it as none (decision 165), the cell asks for a count
    { key: 'onhand', header: t('onHand'), width: '52px', align: 'right', render: (l) => (l.onHand < 0 ? <span title={t('countMeHint')} className="count-me">{formatQty(round1(l.onHand))}<small>{t('countMe')}</small></span> : formatQty(round1(l.onHand))), cellClass: (l) => (l.onHand < 0 ? 'bad' : undefined) },
    { key: 'order', header: t('orderCol'), hint: t('orderColHint'), width: '56px', align: 'right', render: (l) => formatQty(liveTotals(l, accounts).order), cellClass: () => 'order-cell' },
  ];

  // the model wants these but no vendor tab can order them: the item has no home vendor
  const noVendor = lines.filter((l) => !l.vendorId && liveTotals(l, accounts).order > 0);
  // a store's line with no price for it: the invoice is refused until one is published (decision 162)
  const noPrice = lines.filter((l) => l.accounts.some((c) => c.noPrice));
  // the totals follow what is on screen (a search narrows them too)
  const totalOf = (f: (l: Line) => number) => round1(shown.reduce((s, l) => s + f(l), 0));
  const orderLines = lines.map((l) => ({ itemId: l.itemId, qty: liveTotals(l, accounts).order, cost: l.cost })).filter((l) => l.qty > 0);
  const orderPacks = orderLines.reduce((s, l) => s + l.qty, 0);
  const orderCost = orderLines.reduce((s, l) => s + (l.cost ?? 0) * l.qty, 0);
  const footer = d ? (
    <tr>
      <td>{t('totals')}</td>
      <td />
      <td />
      <td />
      {accounts.map((a, ai) => (
        <td key={a.customerId} className="al-right" data-testid={`total-${a.customerId}`}>
          {formatQty(totalOf((l) => cellQty(l, ai, a)))}
        </td>
      ))}
      {Array.from({ length: freeSlots }, (_, i) => <td key={`slot${i}`} />)}
      <td className="al-right" data-testid="total-stores">{formatQty(totalOf((l) => liveTotals(l, accounts).stores))}</td>
      {moreOthers ? <td className="al-right">{formatQty(totalOf((l) => l.others))}</td> : null}
      <td />
      <td className="al-right" data-testid="total-order">{formatQty(totalOf((l) => liveTotals(l, accounts).order))}</td>
    </tr>
  ) : undefined;

  return (
    <div className="order-day-page stack-sm">
      <div className="toolbar">
        <button type="button" className={unsaved > 0 ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'} disabled={unsaved === 0 || saving || badCount > 0} onClick={() => void saveAll()}>
          {saving ? t('sending') : unsaved > 0 ? t('saveNNumbers').replace('{n}', String(unsaved)) : t('allSaved')}
        </button>
        {unsaved > 0 ? <span className="unsaved-note">{t('nNotSaved').replace('{n}', String(unsaved))}</span> : null}
        {badCount > 0 ? <span className="unsaved-note bad" role="alert">{t('nBadNumbers').replace('{n}', String(badCount))}</span> : null}
        <input className="tool-search" type="search" placeholder={t('findItem')} aria-label={t('findItem')} value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="tool check">
          <input type="checkbox" checked={onlyNumbers} onChange={(e) => setOnlyNumbers(e.target.checked)} /> {t('onlyWithNumbers')}
        </label>
        <span className="muted">{t('xOfY').replace('{x}', String(shown.length)).replace('{y}', String(lines.length))}</span>
      </div>
      {lockedTyped.length > 0 ? (
        <div className="warn-band" role="alert">
          {t('lockedTyped').replace('{stores}', lockedTyped.map((a) => `${a.shortName ?? a.customerName} (${t(`status_${a.orderStatus}` as 'status_invoiced')})`).join(', '))}{' '}
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCells((cur) => Object.fromEntries(Object.entries(cur).filter(([k]) => !lockedTyped.some((a) => k.endsWith(`:${a.customerId}`)))))}>
            {t('dropThem')}
          </button>
        </div>
      ) : null}
      {/* the load's vendor shorts, and what a vendor said typed in (decision 176) */}
      <VendorShorts date={date} load={load} />
      {noPrice.length > 0 ? (
        <div className="warn-band" role="alert" data-testid="no-price">
          {t('noPriceWarn').split('{items}')[0]}
          {noPrice.map((l, i) => (
            <span key={l.itemId}>
              {i > 0 ? ', ' : ''}
              {l.itemName}
            </span>
          ))}
          {t('noPriceWarn').split('{items}')[1]} <Link to="/prices">{t('pricesTitle')}</Link>
        </div>
      ) : null}
      {noVendor.length > 0 ? (
        <div className="warn-band" role="alert" data-testid="no-vendor">
          {/* each item opens its form to set the vendor it is bought from */}
          {t('noVendorWarn').split('{items}')[0]}
          {noVendor.map((l, i) => (
            <span key={l.itemId}>
              {i > 0 ? ', ' : ''}
              <Link to={`/items/${l.itemId}?edit=1`}>{l.itemName}</Link> ({formatQty(liveTotals(l, accounts).order)})
            </span>
          ))}
          {t('noVendorWarn').split('{items}')[1]}
        </div>
      ) : null}
      {columnError ? <ErrorBox error={columnError} /> : null}
      {moving ? <BuyFrom key={moving.itemId} line={moving} order={liveTotals(moving, accounts).order} date={date} load={load} onDone={(said) => { setMoving(null); if (said) setNote(said); }} /> : null}
      {saveError ? <ErrorBox error={saveError} /> : null}
      {invoiceAll.isError ? <ErrorBox error={invoiceAll.error} /> : null}
      {note && unsaved === 0 ? (
        <p className="note" role="status">
          {note}
        </p>
      ) : null}
      <QueryView query={day}>
        {(od) =>
          od.rows.length === 0 ? (
            <Empty>{t('noResults')}</Empty>
          ) : (
            <Sheet
              label={t('orderDayTitle')}
              columns={columns}
              rows={shown}
              rowKey={(l) => l.itemId}
              rowLabel={(l) => l.itemName}
              stickyFirst
              minWidth={`${446 + keyCount * 68 + (extras.length + freeSlots) * 80}px`}
              footer={footer}
              onRowsChange={(next, at) => {
                const l = next[at.row]!;
                setNote(null);
                const customerId = at.col.slice('acct:'.length);
                const k = `${l.itemId}:${customerId}`;
                const ai = od.accounts.findIndex((a) => a.customerId === customerId);
                const saved = od.rows.find((r) => r.itemId === l.itemId)!.accounts[ai]!;
                const typed = l.typed[customerId] ?? null;
                const without = <T,>(cur: Record<string, T>) => Object.fromEntries(Object.entries(cur).filter(([ck]) => ck !== k));
                if (l.bad?.[customerId] !== undefined) {
                  setBadCells((cur) => ({ ...cur, [k]: l.bad![customerId]! }));
                  return;
                }
                setBadCells((cur) => without(cur));
                if (saved.source === 'order') {
                  // a real order: a change is something to save; typing back what is saved is not
                  setZeroed((cur) => without(cur));
                  setCells((cur) => ((typed ?? 0) === saved.qty ? without(cur) : { ...without(cur), [k]: typed }));
                } else if ((typed ?? 0) === 0) {
                  // none over a guess: nothing to save (there is no order), but the totals stop counting the guess
                  setCells((cur) => without(cur));
                  setZeroed((cur) => (saved.qty > 0 ? { ...cur, [k]: true } : without(cur)));
                } else {
                  setZeroed((cur) => without(cur));
                  setCells((cur) => ({ ...without(cur), [k]: typed }));
                }
              }}
            />
          )
        }
      </QueryView>
      {d ? (
        <div className="order-day-foot">
          <p className="muted legend">
            <span className="sw sw-unsaved" /> {t('legendUnsaved')} · <span className="sw sw-standing" /> {t('legendStanding')} · <span className="sw sw-pattern" /> {t('legendPattern')}
          </p>
          <span className="spacer" />
          <strong>
            {formatQty(orderPacks)} {t('packs')}
            {d.totalCost !== null ? ` · ${formatMoney(orderCost, intlLocale)}` : ''}
          </strong>
          {books ? (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={invoiceAll.isPending || unsaved > 0 || !d.accounts.some((a) => (a.orderId && a.orderStatus !== 'invoiced') || (a.otherOpen ?? 0) > 0)}
              title={unsaved > 0 ? t('saveFirst') : t('practiceSales')}
              onClick={() => invoiceAll.mutate()}
            >
              {t('invoiceAccounts')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

const MAX_CALL_AHEADS = ORDER_DAY_CALL_AHEADS;
/** the picker's choice that keeps a column that shows only because of an order */
const KEEP = '__keep';

/**
 * Buy an item for this load from another vendor (decision 154): the cases go on that vendor's order for the load and
 * come off the usual vendor's draft, so nothing is bought twice; optionally the vendor it is bought from from now on.
 */
function BuyFrom({ line, order, date, load, onDone }: { line: Line; order: number; date: string; load: number; onDone: (said?: string) => void }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const vendors = useQuery({ queryKey: qk.vendors, queryFn: ({ signal }) => api.vendors(signal), staleTime: 10 * 60_000 });
  const [vendorId, setVendorId] = useState('');
  const onOrders = line.onOrders ?? [];
  // the usual vendor's order for the load, when it carries the item
  const home = line.vendorId ? onOrders.find((o) => o.vendorId === line.vendorId) : undefined;
  const [takeOff, setTakeOff] = useState(!!home && !home.placed);
  // what is still to buy: the ORDER less what other orders carry (the usual vendor's too, unless it comes off it)
  const elsewhere = onOrders.filter((o) => o.vendorId !== vendorId && !(takeOff && o === home)).reduce((s, o) => s + o.qty, 0);
  // the chosen vendor's line is set to this number (not added to)
  const suggested = Math.max(0, Math.round((order - elsewhere) * 10) / 10);
  const [cases, setCases] = useState<string | null>(null);
  const shown = cases ?? (suggested ? String(suggested) : '');
  const [always, setAlways] = useState(false);
  // the price this vendor gave (decision 160): kept on the line; empty, the line takes the offer or the vendor's newest price
  const [price, setPrice] = useState('');
  const go = useMutation({
    mutationFn: async () => {
      const n = parseNumber(shown);
      if (n === null || n < 0) throw new Error(t('buyFromBadQty'));
      const unitCost = price.trim() ? parseNumber(price) : undefined;
      if (unitCost === null || (unitCost !== undefined && unitCost < 0)) throw new Error(t('buyFromBadPrice'));
      // another vendor's price is meant to differ, but more than double or under half the product's cost is likely a
      // typo (145 for 14.5) that would move the auto prices at once (decision 165)
      if (unitCost !== undefined && line.cost !== null && line.cost > 0 && (unitCost > line.cost * 2 || unitCost < line.cost / 2)
        && !window.confirm(t('buyFromPriceFar').replace('{price}', formatMoney(unitCost, intlLocale)).replace('{cost}', formatMoney(line.cost, intlLocale)))) return null;
      // the usual order is placed: buying here too is buying twice unless it is meant
      if (home?.placed && !window.confirm(t('buyFromHomePlaced').replace('{n}', formatQty(home.qty)).replace('{vendor}', home.vendorName))) return null;
      await api.orderDayVendorLine(vendorId, { date, load, itemId: line.itemId, qty: n, ...(takeOff && home ? { takeOffVendorId: home.vendorId } : {}), ...(unitCost !== undefined ? { unitCost } : {}) });
      if (always) await api.patchItem(line.itemId, { homeVendorId: vendorId });
      return n;
    },
    onSuccess: (n) => {
      if (n === null) return;
      for (const k of [['forecast'], ['items']]) void qc.invalidateQueries({ queryKey: k });
      const name = vendors.data?.find((v) => v.id === vendorId)?.name ?? '';
      onDone(t('buyFromSaid').replace('{n}', formatQty(n)).replace('{item}', line.itemName).replace('{vendor}', name));
    },
  });
  return (
    <section className="held-box stack-sm" aria-label={t('buyFromTitle').replace('{item}', line.itemName)}>
      <strong>{t('buyFromTitle').replace('{item}', line.itemName)}</strong>
      <span className="muted small">{t('buyFromHint')}</span>
      {onOrders.length ? (
        <span className="small" data-testid="buy-from-orders">
          {t('buyFromOnOrders')} {onOrders.map((o) => `${formatQty(o.qty)} ${o.vendorName}${o.placed ? ` (${t('buyFromPlaced')})` : ''}`).join(', ')}
        </span>
      ) : null}
      <div className="row-actions">
        <select value={vendorId} onChange={(e) => setVendorId(e.target.value)} aria-label={t('vendor')} style={{ maxWidth: 260 }}>
          <option value="">{t('buyFromPick')}</option>
          {(vendors.data ?? []).filter((v) => v.id !== line.vendorId).map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
        <input type="text" inputMode="decimal" value={shown} onChange={(e) => setCases(e.target.value)} aria-label={t('buyFromCases')} style={{ width: 80 }} />
        <span className="muted">{t('buyFromCases')}</span>
        <input type="text" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} aria-label={t('buyFromPrice')} placeholder={t('buyFromPrice')} style={{ width: 96 }} />
        {home && !home.placed ? (
          <label className="tool check">
            <input type="checkbox" checked={takeOff} onChange={(e) => setTakeOff(e.target.checked)} /> {t('buyFromTakeOff').replace('{n}', formatQty(home.qty)).replace('{vendor}', home.vendorName)}
          </label>
        ) : null}
        <label className="tool check">
          <input type="checkbox" checked={always} onChange={(e) => setAlways(e.target.checked)} /> {t('buyFromAlways')}
        </label>
        <button type="button" className="btn btn-primary btn-sm" disabled={!vendorId || go.isPending} onClick={() => go.mutate()}>
          {t('buyFromGo')}
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onDone()}>
          {t('cancel')}
        </button>
      </div>
      {home?.placed ? (
        <p className="alert alert-warn" role="alert">
          {t('buyFromHomePlaced').replace('{n}', formatQty(home.qty)).replace('{vendor}', home.vendorName)}
        </p>
      ) : null}
      {go.error ? <ErrorBox error={go.error} /> : null}
    </section>
  );
}
